import * as React from 'react'
import Step from '@mui/material/Step'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import { Box, Grid, Popover, Skeleton, Stepper } from '@mui/material'
import { CircularProgressWithLabel } from './CircularProgressWithLabel'
import { useTranslation } from 'react-i18next'
import { useFeature } from '@cbgms/base/feature-flags'
import { useSteps } from 'app/auth/components/onboarding/steps/use-steps'
import { useOnboardingSetup } from 'app/auth/components/hooks/use-onboarding-setup'
import { useGetCurrentShopID } from 'app/shops/hooks/useGetCurrentShopID'
import { OnboardingStepStatus } from '@cbgms/api'
import { useQueryClient } from '@tanstack/react-query'
import { getOnboardingStepDataCacheKey } from 'app/auth/components/hooks/use-get-onboarding-step-data'
import { useLocation } from 'react-router-dom'

export default function SetupWizard() {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [activeStep, setActiveStep] = React.useState(0)
  const shopID = useGetCurrentShopID()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const loc = useLocation()
  const isTopbarUXChangeEnabled = useFeature('topbar-ux-change-for-timer')
  const { persistMode, onboardingInfo, isLoading } = useOnboardingSetup({
    existingShopOnboardingShopID: shopID,
    basePath: loc.pathname
  })
  const { allSteps } = useSteps({
    basePath: '',
    clearOnboardingToken: () => {},
    persistMode,
    onboardingInfo
  })

  const refetch = () => queryClient.invalidateQueries(getOnboardingStepDataCacheKey(onboardingInfo.onboardingMode))
  const filteredSteps = React.useMemo(() => {
    const enabledSteps = allSteps.filter(step => step.CTAComponent && step.stepData?.State !== OnboardingStepStatus.Off)
    return [
      ...enabledSteps.filter(step => step.stepData?.State !== OnboardingStepStatus.Completed),
      ...enabledSteps.filter(step => step.stepData?.State === OnboardingStepStatus.Completed)
    ]
  }, [allSteps])

  const percent = React.useMemo(() => {
    const finishedSteps = filteredSteps ? filteredSteps.filter(step => step.stepData?.State === OnboardingStepStatus.Completed).length : 0

    return Math.trunc((finishedSteps / filteredSteps.length) * 100)
  }, [filteredSteps])

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setActiveStep(0)
    refetch()
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  if (isLoading) {
    return <Skeleton width={30} />
  }
  const open = Boolean(anchorEl)
  if (percent === 100) {
    return null
  }

  return (
    <>
      <Tooltip title={<>{t('SetupWizard.tooltip')}</>}>
        <Button onClick={handleClick} variant='text' sx={{ ...(isTopbarUXChangeEnabled && { padding: '8px' }) }}>
          <CircularProgressWithLabel value={percent} />
        </Button>
      </Tooltip>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ width: 500, p: 1, maxHeight: 750 }}>
          <Stepper activeStep={activeStep} nonLinear orientation='vertical'>
            {filteredSteps.map((step, index) => {
              const completed = step.stepData?.State === OnboardingStepStatus.Completed
              const Component = step.CTAComponent!
              return (
                <Step key={index} completed={completed}>
                  <Component
                    completed={completed}
                    setActiveStep={setActiveStep}
                    index={index}
                    handleClose={handleClose}
                    refetch={refetch}
                  />
                </Step>
              )
            })}
          </Stepper>
          <Grid container>
            <Grid item xs={6}>
              <Button variant='text' disabled={activeStep === 0} onClick={handleBack} sx={{ mt: 1, mr: 1 }} fullWidth>
                {t('SetupWizard.back')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant='text'
                fullWidth
                onClick={handleNext}
                sx={{ mt: 1, mr: 1 }}
                disabled={activeStep === filteredSteps.length - 1}
              >
                {t('SetupWizard.next')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </>
  )
}
